import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { trackCheckInClickHelp } from '../../analytics';

import { Logo } from '../../components/general';
import { PageWrapper } from '../../components/layout/content';

import { PublicEvent, Face, PageError } from '../../types';
import { getEventImg, getHelpUrl, getBpWidth } from '../../utils';

import CheckInContent from './checkInContent';
import MissingAssetsContent from './maContent';

interface Props {
  isPreview?: boolean;
  width: number;
  isLoading?: boolean;
  pageError?: PageError;
  title?: string;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  event: PublicEvent;
  bannerUrl?: string;
  logoUrl?: string;
  face?: Face;
  reqAssets?: string;
  refetchFace?: () => void;
}

const CheckInPageUI: React.FC<Props> = (props) => {
  const {
    isPreview,
    width,
    isLoading = false,
    pageError = null,
    title,
    step,
    setStep,
    event,
    bannerUrl,
    logoUrl,
    face,
    reqAssets,
    refetchFace,
  } = props;

  useEffect(() => {
    if (step > 0 && !isPreview && !face) setStep(0);
  }, [face, isPreview, setStep, step]);

  const renderContent = () => {
    if (!!reqAssets)
      return (
        <MissingAssetsContent
          event={event}
          face={face}
          refetchFace={refetchFace}
          reqAssets={reqAssets}
        />
      );

    return (
      <CheckInContent
        isPreview={isPreview}
        width={width}
        step={step}
        setStep={setStep}
        event={event}
        face={
          face ?? {
            eventId: '',
            elementId: '',
            email: '',
            name: '',
            created: '',
            modified: '',
          }
        }
        refetchFace={refetchFace}
      />
    );
  };

  const isMobileDevice = useMemo(() => width < getBpWidth('sm'), [width]);

  const { bannerClass, containerClass } = useMemo(() => {
    return {
      bannerClass: isMobileDevice ? 'aspect-video' : 'aspect-[20/9]',
      containerClass: isMobileDevice
        ? 'top-[-1rem] w-[90%]'
        : 'top-[-10rem] w-[500px]',
    };
  }, [isMobileDevice]);

  return (
    <PageWrapper
      type="full-page"
      title={title}
      isLoading={isLoading}
      pageError={pageError}
      noTips
    >
      <img
        className={classNames('w-full object-cover', bannerClass)}
        src={bannerUrl ?? getEventImg('banner', event.banner)}
        alt="Event Banner"
      />

      <div
        className={classNames(
          'relative flex flex-col items-center mx-auto gap-7',
          containerClass,
        )}
      >
        <div className="flex flex-col items-center p-4 gap-8 bg-white rounded-md shadow-grey-2">
          <img
            className="w-24 h-24 mt-[-4rem] mb-[-1rem] object-center object-cover rounded-md overflow-hidden"
            src={logoUrl ?? getEventImg('logo', event.logo)}
            alt="Event Logo"
          />

          {renderContent()}

          <Link
            to="https://crowdclip.com"
            target="_blank"
            className="block text-center no-underline hover:opacity-100"
          >
            Powered by <Logo width="80px" noLink />
          </Link>
        </div>

        <div className="text-center text-xs text-[#5e5e5e]">
          Need help? Click{' '}
          <Link
            to={getHelpUrl('attendee', 'check-in')}
            target="_blank"
            onClick={() => trackCheckInClickHelp()}
          >
            here
          </Link>
        </div>
      </div>
    </PageWrapper>
  );
};

export default CheckInPageUI;
